/**
 * Data table for sessions.
 */
import * as React from 'react';
import { Calendar, UserCheck } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Spinner } from '@/components/ui/spinner';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { DataTable } from '@/components/common/data-table';
import { Button } from '@/components/forms/Button';
import { SessionRow } from './SessionRow';
import { SessionCard } from './SessionCard';
import { SessionFilters as SessionFiltersContent } from './SessionFilters';
import {
  areSessionFiltersActive,
  buildInPersonSessionExportParams,
  buildInPersonSessionGroupExportParams,
  buildInPersonSessionGroupListParams,
  buildInPersonSessionListParams,
  buildInPersonStandaloneInformationListParams,
  buildSessionGroupCancelSuccessMessage,
  formatSessionListItemCancelName,
  isInformationMeetingInPersonType,
  mergeGroupsWithStandalone,
  resolveEffectiveTypes,
  resolveGroupDetailsSession,
  resolveInPersonGroupCancelTarget,
  shouldFetchExpandedSessionForGroup,
  shouldMergeStandaloneInformation,
  type SessionItemPageActions,
  type SessionListItem,
} from './session-table-utils';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { DetailsViewContent } from '@/components/common/details/DetailsViewContent';
import { renderSessionDetails } from './renderSessionDetails';
import { CancelSessionModal } from './CancelSessionModal';
import { BaseModal } from '@/components/common/modals/BaseModal';
import { SessionAttendanceModal } from './SessionAttendanceModal';
import { useDataTable } from '@/hooks/useDataTable';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import {
  cancelSession,
  exportSessionsExcel,
  getSession,
  getSessions,
  cancelSessionGroup,
  exportSessionGroupsExcel,
  getSessionGroup,
  getSessionGroups,
} from '@/services/inperson/sessions.service';
import { cn } from '@/lib/utils';
import {
  ALL_FILTER_GROUP_IDS,
  DEFAULT_SESSION_FILTERS,
  FILTER_GROUP_LABELS,
  type FilterGroupId,
  type SessionFilters,
} from '@/components/sessions/session-configs';
import type { ViewMode } from '@/types/data-table.types';
import type { ElementWidth } from '@/types/interface-elements.types';
import {
  type CancelSessionReasonCode,
  type SessionGroup,
  type SessionTab,
  type SessionWithParticipants,
} from '@/types/inperson/session.types';

/** Props for {@link SessionsDataTable}. */
interface SessionsDataTableProps {
  showCreateButton?: boolean;
  onCreateSession?: () => void;
  sessions?: SessionListItem[];
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  expandedRowId?: string | null;
  onToggleRow?: (id: string) => void;
  onCloseDetails?: () => void;
  onEditSession?: (id: string, sessionType?: string) => void;
  onEditGroup?: (group: SessionGroup) => void;
  enableFilters?: boolean;
  defaultFilters?: SessionFilters;
  layoutWidth?: ElementWidth;
  isSection?: boolean;
  activeTab?: 'all' | SessionTab;
  /** RBAC: tabs the current user is allowed to see (restricts the all-tab query). */
  allowedTabIds?: SessionTab[];
  toolbarLeft?: React.ReactNode;
  /** Extra nodes rendered at the start of the section-actions bar (before filter/export/create). */
  toolbarExtra?: React.ReactNode;
  pageActions?: SessionItemPageActions;
  onFiltersChange?: (filters: SessionFilters) => void;
}

/** Typed {@link useDataTable} handle shared by toolbar, filters, and table chrome. */
type SessionsDt = ReturnType<typeof useDataTable<SessionFilters, FilterGroupId>>;

/** Group details: information meetings render the first child; others render the group. */
function renderGroupDetails(
  group: SessionGroup,
  expandedSessionData: SessionWithParticipants | undefined,
): React.ReactNode {
  return (
    <DetailsViewContent>
      {renderSessionDetails(resolveGroupDetailsSession(group, expandedSessionData))}
    </DetailsViewContent>
  );
}

/** Fixed column headers for the in-person sessions table. */
function SessionsTableHead() {
  return (
    <tr>
      <th>Date &amp; Horaire</th>
      <th className="text-center">Type</th>
      <th>Établissement</th>
      <th>Statut</th>
      <th className="w-28 text-center">Actions</th>
    </tr>
  );
}

/** Configure-panel chips — toggle which filter groups are visible. */
function FilterVisibilityButtons({ dt }: Readonly<{ dt: SessionsDt }>) {
  return (
    <div className="flex flex-wrap gap-3">
      {ALL_FILTER_GROUP_IDS.filter((id) => dt.allowedFilterIds.includes(id)).map((id) => {
        const isOn = dt.filterVisibility[id] ?? true;
        const activeClass =
          'bg-[var(--color-2-nuance-1)] border-[var(--color-2-nuance-1)] text-white';
        const inactiveClass =
          'bg-transparent border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)] text-muted-foreground hover:border-[var(--color-1-nuance-2)]';
        return (
          <Button
            key={id}
            variant="custom"
            size="sm"
            onClick={() => dt.handleToggleFilterVisibility(id)}
            disabled={dt.isSavingFilterVisibility}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
              dt.isSavingFilterVisibility && 'opacity-60 cursor-not-allowed',
              isOn ? activeClass : inactiveClass,
            )}
          >
            {FILTER_GROUP_LABELS[id]}
          </Button>
        );
      })}
    </div>
  );
}

/** Filter / export / create toolbar actions. Closing create also collapses the detail row. */
function SessionsToolbar({
  dt,
  enableFilters,
  hasActiveFilters,
  showExport,
  handleExportExcel,
  isExporting,
  isEmptyForExport,
  showCreateButton,
  onCreateSession,
  onCloseDetails,
}: Readonly<{
  dt: SessionsDt;
  enableFilters: boolean;
  hasActiveFilters: boolean;
  showExport: boolean;
  handleExportExcel: () => void;
  isExporting: boolean;
  isEmptyForExport: boolean;
  showCreateButton: boolean;
  onCreateSession?: () => void;
  onCloseDetails?: () => void;
}>) {
  const exportTooltip =
    isEmptyForExport && !isExporting ? getErrorMessage('exportEmpty') : 'Exporter en Excel';

  const toggleFilters = () => {
    const next = !dt.showFilters;
    dt.setShowFilters(next);
    if (!next) dt.setShowConfigure(false);
  };

  return (
    <>
      {enableFilters && (
        <Button
          preset="toolbar-filter"
          className={cn(dt.showFilters && 'section-action-button-active')}
          showBadge={hasActiveFilters}
          onClick={toggleFilters}
        />
      )}
      {showExport && (
        <Button
          preset="toolbar-export"
          onClick={handleExportExcel}
          loading={isExporting}
          disabled={isEmptyForExport}
          tooltipText={exportTooltip}
        />
      )}
      {showCreateButton && onCreateSession && (
        <Button
          preset="toolbar-create"
          tooltipText="Planification des séances"
          onClick={() => {
            onCloseDetails?.();
            onCreateSession();
          }}
        />
      )}
    </>
  );
}

/** Filter panel + configure chips. Hidden when `enableFilters` is false. */
function SessionsFiltersPanel({
  enableFilters,
  dt,
  hasActiveFilters,
  filterGroups,
  clearFilters,
  totalItems,
}: Readonly<{
  enableFilters: boolean;
  dt: SessionsDt;
  hasActiveFilters: boolean;
  filterGroups: Array<{
    id: string;
    label: string;
    type: 'custom';
    customRender: () => React.ReactNode;
  }>;
  clearFilters: () => void;
  totalItems: number;
}>) {
  if (!enableFilters) return undefined;
  const canConfigure = dt.allowedFilterIds.length > 0;
  return (
    <DataTableFilters
      show={dt.showFilters}
      hasActiveFilters={hasActiveFilters}
      filterGroups={filterGroups}
      onClearFilters={clearFilters}
      resultCount={totalItems}
      resultLabel="séance(s)"
      onToggleConfigure={canConfigure ? () => dt.setShowConfigure((v) => !v) : undefined}
      showConfigure={dt.showConfigure}
      configureContent={<FilterVisibilityButtons dt={dt} />}
    />
  );
}

/** Attendance modal, or a spinner modal while GET session is still loading. */
function AttendanceOrLoadingModal({
  attendanceSessionId,
  attendanceSession,
  onClose,
  onSuccess,
}: Readonly<{
  attendanceSessionId: string | null;
  attendanceSession: SessionWithParticipants | undefined;
  onClose: () => void;
  onSuccess: () => void;
}>) {
  if (!attendanceSessionId) return null;
  if (attendanceSession) {
    return (
      <SessionAttendanceModal
        open={true}
        onClose={onClose}
        session={attendanceSession}
        onSuccess={onSuccess}
      />
    );
  }
  return (
    <BaseModal
      open={true}
      onClose={onClose}
      size="md"
      header={{
        preset: 'validation',
        icon: UserCheck,
        iconSize: 'md',
        title: 'Gestion des présences',
        titleSize: 'lg',
        titleWeight: 'semibold',
      }}
    >
      <div className="flex items-center justify-center py-8">
        <Spinner size="lg" label="Chargement des participants..." />
      </div>
    </BaseModal>
  );
}

/** Filter setters that always reset pagination to page 1. */
function useFilterHandlers(dt: SessionsDt) {
  const toggleTypeFilter = React.useCallback(
    (type: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        types: prev.types.includes(type)
          ? prev.types.filter((t) => t !== type)
          : [...prev.types, type],
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );
  const toggleStatusFilter = React.useCallback(
    (status: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        statuses: prev.statuses.includes(status)
          ? prev.statuses.filter((s) => s !== status)
          : [...prev.statuses, status],
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );
  const setDateRange = React.useCallback(
    (range: SessionFilters['dateRange']) => {
      dt.setFilters((prev) => ({ ...prev, dateRange: range }));
      dt.setCurrentPage(1);
    },
    [dt],
  );
  const setCustomDateRange = React.useCallback(
    (from: Date | undefined, to: Date | undefined) => {
      dt.setFilters((prev) => ({
        ...prev,
        customDateFrom: from,
        customDateTo: to,
        dateRange: from || to ? 'all' : prev.dateRange,
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );
  const setEstablishment = React.useCallback(
    (id: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, establishmentId: id }));
      dt.setCurrentPage(1);
    },
    [dt],
  );
  const setSubject = React.useCallback(
    (id: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, subjectId: id }));
      dt.setCurrentPage(1);
    },
    [dt],
  );
  const setLevel = React.useCallback(
    (id: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, levelId: id }));
      dt.setCurrentPage(1);
    },
    [dt],
  );
  const setSearch = React.useCallback(
    (value: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, search: value }));
      dt.setCurrentPage(1);
    },
    [dt],
  );
  const setRecurrenceType = React.useCallback(
    (type: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, recurrenceType: type }));
      dt.setCurrentPage(1);
    },
    [dt],
  );
  const setAnimatorFilter = React.useCallback(
    (id: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, animatorUserId: id }));
      dt.setCurrentPage(1);
    },
    [dt],
  );
  return {
    toggleTypeFilter,
    toggleStatusFilter,
    setDateRange,
    setCustomDateRange,
    setEstablishment,
    setSubject,
    setLevel,
    setSearch,
    setRecurrenceType,
    setAnimatorFilter,
  };
}

/** Shared toolbar / filter / pagination props for {@link GroupsTable} and {@link SessionsTable}. */
interface SharedTableChrome {
  dt: SessionsDt;
  enableFilters: boolean;
  hasActiveFilters: boolean;
  showExport: boolean;
  handleExportExcel: () => void;
  isExporting: boolean;
  isEmptyForExport: boolean;
  showCreateButton: boolean;
  onCreateSession?: () => void;
  onCloseDetails?: () => void;
  filterGroups: Array<{
    id: string;
    label: string;
    type: 'custom';
    customRender: () => React.ReactNode;
  }>;
  clearFilters: () => void;
  totalItems: number;
  totalPages: number;
  startIndex: number;
  endIndex: number;
  isLoading: boolean;
  isSection: boolean;
  pageActions?: SessionItemPageActions;
  handleManageAttendance: (id: string) => void;
  onToggleRow?: (id: string) => void;
  onEditSession?: (id: string, sessionType?: string) => void;
  /** Extra nodes rendered at the start of the section-actions bar (before filter/export/create). */
  toolbarExtra?: React.ReactNode;
}

/** Group-mode table (parent groups) — used for tutoring / collective types. */
function GroupsTable({
  chrome,
  groupsData,
  expandedGroup,
  expandedSessionData,
  isExpandedDetailLoading,
  toolbarLeft,
  onEditGroup,
  setCancelId,
  setCancelGroupId,
  setCancelGroupChildCount,
  cancelGroupId,
  cancelGroupChildCount,
  cancelId,
  cancelGroupMutation,
  cancelMutation,
  attendanceSessionId,
  attendanceSession,
  onCloseAttendance,
  onAttendanceSuccess,
}: Readonly<{
  chrome: SharedTableChrome;
  groupsData: SessionGroup[];
  expandedGroup: SessionGroup | null;
  expandedSessionData: SessionWithParticipants | undefined;
  isExpandedDetailLoading?: boolean;
  toolbarLeft?: React.ReactNode;
  onEditGroup?: (group: SessionGroup) => void;
  setCancelId: (id: string | null) => void;
  setCancelGroupId: (id: string | null) => void;
  setCancelGroupChildCount: (count: number) => void;
  cancelGroupId: string | null;
  cancelGroupChildCount: number;
  cancelId: string | null;
  cancelGroupMutation: {
    mutate: (p: { id: string; reasonCode: CancelSessionReasonCode; reasonDetail?: string }) => void;
    isPending: boolean;
  };
  cancelMutation: {
    mutate: (p: { id: string; reasonCode: string; reasonDetail?: string }) => void;
    isPending: boolean;
  };
  attendanceSessionId: string | null;
  attendanceSession: SessionWithParticipants | undefined;
  onCloseAttendance: () => void;
  onAttendanceSuccess: () => void;
}>) {
  const attendanceHandler =
    !chrome.pageActions || chrome.pageActions.canManageAttendance
      ? chrome.handleManageAttendance
      : undefined;

  const onCancelGroup = (group: SessionGroup, id: string, count: number) => {
    if (resolveInPersonGroupCancelTarget(group.type) === 'session') {
      setCancelId(id);
      return;
    }
    setCancelGroupId(id);
    setCancelGroupChildCount(count);
  };

  return (
    <DataTable
      data={groupsData}
      totalItems={chrome.totalItems}
      isLoading={chrome.isLoading}
      viewMode={chrome.dt.viewMode}
      onViewModeChange={chrome.dt.handleViewModeChange}
      currentPage={chrome.dt.currentPage}
      totalPages={chrome.totalPages}
      startIndex={chrome.startIndex}
      endIndex={chrome.endIndex}
      itemLabel="groupes de séances"
      onPageChange={chrome.dt.setCurrentPage}
      hasActiveFilters={chrome.hasActiveFilters}
      containerRef={chrome.dt.containerRef}
      expandedColSpan={5}
      toolbarLeft={toolbarLeft}
      toolbarExtra={chrome.toolbarExtra}
      toolbarActions={<SessionsToolbar {...chrome} />}
      filtersPanel={<SessionsFiltersPanel {...chrome} />}
      renderTableHead={SessionsTableHead}
      renderTableRow={(group) => (
        <SessionRow
          group={group}
          onView={chrome.onToggleRow ? () => chrome.onToggleRow?.(group.id) : undefined}
          onEdit={(id) => chrome.onEditSession?.(id, group.type)}
          onEditGroup={group.type === 'information_meeting_in_person' ? undefined : onEditGroup}
          onCancelGroup={(id, count) => onCancelGroup(group, id, count)}
          onManageAttendance={attendanceHandler}
          pageActions={chrome.pageActions}
        />
      )}
      expandedRowId={chrome.dt.expandedRowId}
      renderExpandedRow={(group) => renderGroupDetails(group, expandedSessionData)}
      expandedRowRef={chrome.dt.expandedRowRef}
      expandedCollapseRef={chrome.dt.expandedCollapseRef}
      renderCard={(group) => (
        <SessionCard
          group={group}
          onView={chrome.onToggleRow ? () => chrome.onToggleRow?.(group.id) : undefined}
          onEdit={(id) => chrome.onEditSession?.(id, group.type)}
          onEditGroup={group.type === 'information_meeting_in_person' ? undefined : onEditGroup}
          onCancelGroup={(id, count) => onCancelGroup(group, id, count)}
          onManageAttendance={attendanceHandler}
          pageActions={chrome.pageActions}
        />
      )}
      emptyIcon={Calendar}
      emptyText="Aucune séance trouvée"
      title="Séances"
      sidebarMenuId="menu-sessions"
      isSection={chrome.isSection}
      expandedItem={expandedGroup}
      isExpandedDetailLoading={isExpandedDetailLoading}
      onCloseDetails={chrome.onCloseDetails}
      renderDetailsContent={(group) => renderGroupDetails(group, expandedSessionData)}
      modals={
        <>
          <CancelSessionModal
            open={!!cancelGroupId}
            onClose={() => setCancelGroupId(null)}
            onConfirm={(reasonCode: CancelSessionReasonCode, reasonDetail?: string) => {
              if (cancelGroupId) {
                cancelGroupMutation.mutate({ id: cancelGroupId, reasonCode, reasonDetail });
              }
            }}
            isGroupCancel
            childCount={cancelGroupChildCount}
            isLoading={cancelGroupMutation.isPending}
          />
          <CancelSessionModal
            open={!!cancelId}
            onClose={() => setCancelId(null)}
            onConfirm={(reasonCode: CancelSessionReasonCode, reasonDetail?: string) => {
              if (cancelId) cancelMutation.mutate({ id: cancelId, reasonCode, reasonDetail });
            }}
            isLoading={cancelMutation.isPending}
          />
          <AttendanceOrLoadingModal
            attendanceSessionId={attendanceSessionId}
            attendanceSession={attendanceSession}
            onClose={onCloseAttendance}
            onSuccess={onAttendanceSuccess}
          />
        </>
      }
    />
  );
}

/** Child-session table (non-group rows / information meetings). */
function SessionsTable({
  chrome,
  sessionsData,
  expandedItem,
  isExpandedDetailLoading,
  cancelId,
  setCancelId,
  cancelMutation,
  sessionToCancel,
  attendanceSessionId,
  attendanceSession,
  onCloseAttendance,
  onAttendanceSuccess,
}: Readonly<{
  chrome: SharedTableChrome;
  sessionsData: SessionListItem[];
  expandedItem: SessionListItem | null;
  isExpandedDetailLoading?: boolean;
  cancelId: string | null;
  setCancelId: (id: string | null) => void;
  cancelMutation: {
    mutate: (p: { id: string; reasonCode: string; reasonDetail?: string }) => void;
    isPending: boolean;
  };
  sessionToCancel: SessionListItem | undefined;
  attendanceSessionId: string | null;
  attendanceSession: SessionWithParticipants | undefined;
  onCloseAttendance: () => void;
  onAttendanceSuccess: () => void;
}>) {
  const attendanceHandler =
    !chrome.pageActions || chrome.pageActions.canManageAttendance
      ? chrome.handleManageAttendance
      : undefined;
  const cancelName = sessionToCancel ? formatSessionListItemCancelName(sessionToCancel) : undefined;

  return (
    <DataTable
      data={sessionsData}
      totalItems={chrome.totalItems}
      isLoading={chrome.isLoading}
      viewMode={chrome.dt.viewMode}
      onViewModeChange={chrome.dt.handleViewModeChange}
      currentPage={chrome.dt.currentPage}
      totalPages={chrome.totalPages}
      startIndex={chrome.startIndex}
      endIndex={chrome.endIndex}
      itemLabel="séances"
      onPageChange={chrome.dt.setCurrentPage}
      hasActiveFilters={chrome.hasActiveFilters}
      containerRef={chrome.dt.containerRef}
      expandedColSpan={5}
      toolbarExtra={chrome.toolbarExtra}
      toolbarActions={<SessionsToolbar {...chrome} />}
      filtersPanel={<SessionsFiltersPanel {...chrome} />}
      renderTableHead={SessionsTableHead}
      renderTableRow={(session) => (
        <SessionRow
          session={session}
          onView={chrome.onToggleRow ? () => chrome.onToggleRow?.(session.id) : undefined}
          onEdit={(id) => chrome.onEditSession?.(id, session.type)}
          onCancel={(id) => setCancelId(id)}
          onManageAttendance={attendanceHandler}
          pageActions={chrome.pageActions}
        />
      )}
      expandedRowId={chrome.dt.expandedRowId}
      renderExpandedRow={(session) => (
        <DetailsViewContent>
          {renderSessionDetails(session as SessionWithParticipants)}
        </DetailsViewContent>
      )}
      expandedRowRef={chrome.dt.expandedRowRef}
      expandedCollapseRef={chrome.dt.expandedCollapseRef}
      renderCard={(session) => (
        <SessionCard
          session={session}
          onView={chrome.onToggleRow ? () => chrome.onToggleRow?.(session.id) : undefined}
          onEdit={(id) => chrome.onEditSession?.(id, session.type)}
          onCancel={(id) => setCancelId(id)}
          onManageAttendance={attendanceHandler}
          pageActions={chrome.pageActions}
        />
      )}
      emptyIcon={Calendar}
      emptyText="Aucune séance trouvée"
      title="Séances"
      sidebarMenuId="menu-sessions"
      isSection={chrome.isSection}
      expandedItem={expandedItem}
      isExpandedDetailLoading={isExpandedDetailLoading}
      onCloseDetails={chrome.onCloseDetails}
      renderDetailsContent={(session) => (
        <DetailsViewContent>
          {renderSessionDetails(session as SessionWithParticipants)}
        </DetailsViewContent>
      )}
      modals={
        <>
          <CancelSessionModal
            open={!!cancelId}
            onClose={() => setCancelId(null)}
            onConfirm={(reasonCode: CancelSessionReasonCode, reasonDetail?: string) => {
              if (cancelId) cancelMutation.mutate({ id: cancelId, reasonCode, reasonDetail });
            }}
            itemName={cancelName}
            isLoading={cancelMutation.isPending}
          />
          <AttendanceOrLoadingModal
            attendanceSessionId={attendanceSessionId}
            attendanceSession={attendanceSession}
            onClose={onCloseAttendance}
            onSuccess={onAttendanceSuccess}
          />
        </>
      }
    />
  );
}

/** Data table for sessions. */
export const SessionsDataTable: React.FC<SessionsDataTableProps> = ({
  sessions: externalSessions,
  showCreateButton = false,
  onCreateSession,
  viewMode: externalViewMode,
  onViewModeChange: externalOnViewModeChange,
  expandedRowId,
  onToggleRow,
  onCloseDetails,
  onEditSession,
  onEditGroup,
  enableFilters = true,
  defaultFilters = DEFAULT_SESSION_FILTERS,
  layoutWidth: layoutWidthProp = 'full',
  isSection = false,
  activeTab,
  allowedTabIds,
  toolbarLeft,
  toolbarExtra,
  pageActions,
  onFiltersChange,
}) => {
  const queryClient = useQueryClient();
  const showExport = pageActions?.canExportCsv === true;
  const [cancelId, setCancelId] = React.useState<string | null>(null);
  const [cancelGroupId, setCancelGroupId] = React.useState<string | null>(null);
  const [cancelGroupChildCount, setCancelGroupChildCount] = React.useState(0);
  const [attendanceSessionId, setAttendanceSessionId] = React.useState<string | null>(null);
  const [isExporting, setIsExporting] = React.useState(false);

  const dt = useDataTable<SessionFilters, FilterGroupId>({
    tableName: 'sessions',
    defaultFilters,
    allFilterGroupIds: ALL_FILTER_GROUP_IDS,
    layoutWidth: layoutWidthProp,
    isSection,
    externalViewMode,
    onExternalViewModeChange: externalOnViewModeChange,
    externalExpandedRowId: expandedRowId,
    onCloseDetails,
    onFiltersChange,
  });

  // Reset to page 1 and close detail row when tab changes — compare with previous value to skip
  // initial mount (and React 18 StrictMode's double-invoke).
  const prevActiveTabRef = React.useRef(activeTab);
  React.useEffect(() => {
    const prev = prevActiveTabRef.current;
    prevActiveTabRef.current = activeTab;
    if (prev === activeTab) return;
    dt.closeExpandedRow();
    dt.setCurrentPage(1);
  }, [activeTab]);

  const effectiveTypes = React.useMemo(
    () => resolveEffectiveTypes(activeTab, allowedTabIds, dt.filters.types),
    [activeTab, allowedTabIds, dt.filters.types],
  );

  const isInformationTab = activeTab === 'information-meeting';
  const mergeStandalone = shouldMergeStandaloneInformation(
    !!externalSessions,
    isInformationTab,
    activeTab,
    allowedTabIds,
  );

  const { data: groupsResponse, isLoading: isLoadingGroups } = useQuery({
    queryKey: ['session-groups', dt.currentPage, dt.itemsPerPage, dt.filters, activeTab],
    queryFn: async () => {
      return await getSessionGroups(
        buildInPersonSessionGroupListParams(dt.filters, {
          page: dt.currentPage,
          pageSize: dt.itemsPerPage,
          types: effectiveTypes,
        }),
      );
    },
    enabled: !externalSessions && !isInformationTab,
  });

  const { data: sessionsResponse, isLoading: isLoadingSessions } = useQuery({
    queryKey: ['sessions', dt.currentPage, dt.itemsPerPage, dt.filters, activeTab],
    queryFn: async () => {
      return await getSessions(
        buildInPersonSessionListParams(dt.filters, {
          page: dt.currentPage,
          pageSize: dt.itemsPerPage,
          types: effectiveTypes,
        }),
      );
    },
    enabled: !!externalSessions || isInformationTab,
  });

  const { data: standaloneInformationResponse, isLoading: isLoadingStandaloneInformation } =
    useQuery({
      queryKey: ['sessions', 'information-standalone', dt.filters, dt.currentPage],
      queryFn: async () => {
        return await getSessions(buildInPersonStandaloneInformationListParams(dt.filters));
      },
      enabled: mergeStandalone,
    });

  const useGroupMode = !externalSessions && !isInformationTab;
  const isLoading = useGroupMode
    ? isLoadingGroups || (mergeStandalone && isLoadingStandaloneInformation)
    : isLoadingSessions;

  const groupsData: SessionGroup[] = React.useMemo(() => {
    const groups = externalSessions ? [] : (groupsResponse?.data ?? []);
    const standalone = mergeStandalone ? (standaloneInformationResponse?.data ?? []) : [];
    return mergeGroupsWithStandalone(groups, standalone as SessionListItem[]);
  }, [externalSessions, groupsResponse, standaloneInformationResponse, mergeStandalone]);

  const sessionsData: SessionListItem[] = React.useMemo(
    () => (externalSessions ?? sessionsResponse?.data ?? []) as SessionListItem[],
    [externalSessions, sessionsResponse],
  );

  const totalItems = useGroupMode
    ? (groupsResponse?.total ?? 0) + (standaloneInformationResponse?.total ?? 0)
    : (sessionsResponse?.total ?? sessionsData.length);
  const totalPages = Math.ceil(totalItems / dt.itemsPerPage) || 1;
  const startIndex = (dt.currentPage - 1) * dt.itemsPerPage;
  const endIndex = startIndex + dt.itemsPerPage;
  const isEmptyForExport = !isLoading && totalItems === 0;

  /** Download the current filter set as Excel (same query as the list, no pagination). */
  const handleExportExcel = React.useCallback(async () => {
    if (isEmptyForExport) {
      toast.warning(getErrorMessage('exportEmpty'));
      return;
    }
    setIsExporting(true);
    try {
      if (useGroupMode) {
        await exportSessionGroupsExcel(
          buildInPersonSessionGroupExportParams(dt.filters, effectiveTypes),
        );
      } else {
        await exportSessionsExcel(buildInPersonSessionExportParams(dt.filters, effectiveTypes));
      }
      toast.success('Export Excel réussi !');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsExporting(false);
    }
  }, [dt.filters, effectiveTypes, isEmptyForExport, useGroupMode]);

  const {
    data: expandedGroupData,
    isLoading: isLoadingExpandedGroup,
    isError: isExpandedGroupError,
    error: expandedGroupError,
  } = useQuery({
    queryKey: ['session-group', expandedRowId],
    queryFn: () => getSessionGroup(expandedRowId!),
    enabled: useGroupMode && !!expandedRowId,
    retry: shouldRetryUnitGet,
  });

  const {
    data: expandedSessionData,
    isLoading: isLoadingExpandedSession,
    isError: isExpandedSessionError,
    error: expandedSessionError,
  } = useQuery({
    queryKey: ['session', expandedRowId],
    queryFn: () => getSession(expandedRowId!),
    enabled:
      !!expandedRowId && shouldFetchExpandedSessionForGroup(useGroupMode, expandedGroupData?.type),
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError: isExpandedGroupError || isExpandedSessionError,
    error: expandedGroupError ?? expandedSessionError,
    catalogKey: 'sessionNotFound',
    onClose: onCloseDetails,
    entityId: expandedRowId,
  });

  const hasActiveFilters = React.useMemo(
    () => areSessionFiltersActive(dt.filters, defaultFilters),
    [dt.filters, defaultFilters],
  );

  const clearFilters = React.useCallback(() => {
    dt.setFilters(defaultFilters);
    dt.setCurrentPage(1);
  }, [dt, defaultFilters]);

  const cancelMutation = useMutation({
    mutationFn: ({
      id,
      reasonCode,
      reasonDetail,
    }: {
      id: string;
      reasonCode: string;
      reasonDetail?: string;
    }) => cancelSession(id, { reasonCode, reasonDetail }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sessions'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['session-groups'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['vsc-reports'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['vsc-report'], refetchType: 'all' }),
      ]);
      setCancelId(null);
      toast.success('Séance annulée avec succès !');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const cancelGroupMutation = useMutation({
    mutationFn: ({
      id,
      reasonCode,
      reasonDetail,
    }: {
      id: string;
      reasonCode: CancelSessionReasonCode;
      reasonDetail?: string;
    }) => cancelSessionGroup(id, { reasonCode, reasonDetail }),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['session-groups'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['sessions'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['vsc-reports'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['vsc-report'], refetchType: 'all' }),
      ]);
      setCancelGroupId(null);
      toast.success(
        buildSessionGroupCancelSuccessMessage(result.cancelledCount, result.skippedCount),
      );
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const { data: attendanceSession } = useQuery({
    queryKey: ['session', attendanceSessionId],
    queryFn: () => getSession(attendanceSessionId ?? ''),
    enabled: !!attendanceSessionId,
  });

  /** Open the attendance modal for the given session. */
  const handleManageAttendance = React.useCallback((id: string) => {
    setAttendanceSessionId(id);
  }, []);

  /** Refresh session and group lists after attendance is saved. */
  const handleAttendanceSuccess = React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['sessions'] });
    void queryClient.invalidateQueries({ queryKey: ['session-groups'] });
  }, [queryClient]);

  const sessionToCancel = React.useMemo(
    () => sessionsData.find((s) => s.id === cancelId),
    [sessionsData, cancelId],
  );

  const filterHandlers = useFilterHandlers(dt);
  const customFilterRender = React.useCallback(
    () => (
      <SessionFiltersContent
        filters={dt.filters}
        allowedFilterIds={dt.allowedFilterIds}
        visibleFilterIds={dt.visibleFilterIds}
        activeTab={activeTab}
        onToggleType={filterHandlers.toggleTypeFilter}
        onToggleStatus={filterHandlers.toggleStatusFilter}
        onSetDateRange={filterHandlers.setDateRange}
        onSetCustomDateRange={filterHandlers.setCustomDateRange}
        onSetEstablishment={filterHandlers.setEstablishment}
        onSetSubject={filterHandlers.setSubject}
        onSetLevel={filterHandlers.setLevel}
        onSetSearch={filterHandlers.setSearch}
        onSetRecurrenceType={filterHandlers.setRecurrenceType}
        onSetAnimatorFilter={filterHandlers.setAnimatorFilter}
      />
    ),
    [dt.filters, dt.allowedFilterIds, dt.visibleFilterIds, activeTab, filterHandlers],
  );

  const filterGroups = React.useMemo(
    () => [
      {
        id: 'filters',
        label: 'Filtres',
        type: 'custom' as const,
        customRender: customFilterRender,
      },
    ],
    [customFilterRender],
  );

  const chrome: SharedTableChrome = {
    dt,
    enableFilters,
    hasActiveFilters,
    showExport,
    handleExportExcel,
    isExporting,
    isEmptyForExport,
    showCreateButton,
    onCreateSession,
    onCloseDetails,
    filterGroups,
    clearFilters,
    totalItems,
    totalPages,
    startIndex,
    endIndex,
    isLoading,
    isSection,
    pageActions,
    handleManageAttendance,
    onToggleRow,
    onEditSession,
    toolbarExtra,
  };

  if (useGroupMode) {
    const expandedGroup = expandedGroupData ?? null;
    return (
      <GroupsTable
        chrome={chrome}
        groupsData={groupsData}
        expandedGroup={expandedGroup}
        expandedSessionData={expandedSessionData}
        isExpandedDetailLoading={
          isLoadingExpandedGroup ||
          (isInformationMeetingInPersonType(expandedGroupData?.type) && isLoadingExpandedSession)
        }
        toolbarLeft={toolbarLeft}
        onEditGroup={onEditGroup}
        setCancelId={setCancelId}
        setCancelGroupId={setCancelGroupId}
        setCancelGroupChildCount={setCancelGroupChildCount}
        cancelGroupId={cancelGroupId}
        cancelGroupChildCount={cancelGroupChildCount}
        cancelId={cancelId}
        cancelGroupMutation={cancelGroupMutation}
        cancelMutation={cancelMutation}
        attendanceSessionId={attendanceSessionId}
        attendanceSession={attendanceSession}
        onCloseAttendance={() => setAttendanceSessionId(null)}
        onAttendanceSuccess={handleAttendanceSuccess}
      />
    );
  }

  const expandedItem = expandedSessionData ?? null;

  return (
    <SessionsTable
      chrome={chrome}
      sessionsData={sessionsData}
      expandedItem={expandedItem as SessionListItem | null}
      isExpandedDetailLoading={isLoadingExpandedSession}
      cancelId={cancelId}
      setCancelId={setCancelId}
      cancelMutation={cancelMutation}
      sessionToCancel={sessionToCancel}
      attendanceSessionId={attendanceSessionId}
      attendanceSession={attendanceSession}
      onCloseAttendance={() => setAttendanceSessionId(null)}
      onAttendanceSuccess={handleAttendanceSuccess}
    />
  );
};
